import { DownOutlined } from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { Button, Collapse, Spin } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { BuyerCenterCartLine } from 'services/api/jolectBuyerCenter';
import { queries } from 'services/queries';
import { ProductInfo } from './ProductInfo';
import { PanelHeader } from './PanelHeader';

interface ShoppingCartPanelProps {
  storeId: Conversation.Conversation['storeId'];
  externalBuyerId: Conversation.Conversation['externalBuyerId'];
}

const adaptCartLine = (line: BuyerCenterCartLine): Conversation.Product => {
  const price = Number(line.merchandise.price.amount) || 0;
  return {
    internalStoreId: '',
    externalShopId: '',
    internalItemId: 0,
    externalItemId: line.merchandise.id,
    itemName: line.merchandise.product.title,
    itemUrl: line.merchandise.product.url,
    imageUrl: line.merchandise.image?.src ? [line.merchandise.image.src] : [],
    itemStatus: 'UNKNOWN',
    createTimeInMs: 0,
    updateTimeInMs: 0,
    isPreOrder: false,
    currentStock: 0,
    sale: 0,
    unit: line.merchandise.price.currencyCode,
    originalLowPrice: price,
    originalHighPrice: price,
    skuLowPrice: price,
    skuHighPrice: price,
    recentConsulting: false,
    itemSku: line.merchandise.sku ?? '',
    skuInfo: [],
    spuStockStatus: 'InStock',
    stockRuleOperateLogData: {
      operateType: 'ADD_RULE',
      estimatedAvailableTime: '',
      createTimeMs: '',
      operateUserId: 0,
      operateUserName: '',
    },
    estimatedAvailableTime: '',
    stockRuleId: '',
    stockFlag: false,
    currency: line.merchandise.price.currencyCode,
  };
};

export const ShoppingCartPanel = (props: ShoppingCartPanelProps) => {
  const { storeId, externalBuyerId } = props;
  const { t } = useTranslate();
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);

  const {
    data: footprintData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    ...queries.jolectBuyerCenter.footprint({
      storeId: Number(storeId) || 0,
      sgNewClientId: externalBuyerId || '',
    }),
    enabled: !!externalBuyerId && !!storeId,
  });

  const items = footprintData?.cart?.lines?.slice(0, 4) ?? [];
  const hasItems = items.length > 0;

  useEffect(() => {
    setActiveKey(hasItems ? 'ShoppingCart' : undefined);
  }, [hasItems]);

  return (
    <Collapse
      activeKey={activeKey}
      className={cx(styles, 'flex-shrink-0')}
      bordered={false}
      expandIconPosition="right"
      expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
      onChange={(key) => setActiveKey(key as string)}
    >
      <Collapse.Panel
        header={
          <PanelHeader
            title={t('scene.chatWorkspace.buyerActivityTab.shoppingCart.title')}
            count={items.length}
          />
        }
        key="ShoppingCart"
      >
        {isError ? (
          <div className="flex flex-col items-center gap-2 py-6 text-[#B5B9BF]">
            <span>{t('common.feedback.loadFailedRetry')}</span>
            <Button type="link" onClick={() => refetch()}>
              {t('common.action.retry')}
            </Button>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-6">
            <Spin />
          </div>
        ) : hasItems ? (
          items.map((line) => (
            <div key={line.merchandise.id} className="py-3">
              <ProductInfo storeId={storeId} product={adaptCartLine(line)} />
            </div>
          ))
        ) : (
          <div className="text-center text-[#B5B9BF]">
            {t('scene.chatWorkspace.buyerActivityTab.shoppingCart.emptyDescription')}
          </div>
        )}
      </Collapse.Panel>
    </Collapse>
  );
};

const styles = css`
  background-color: #ffffff;

  .ant-collapse-item {
    border: none;
  }

  .ant-collapse-item > .ant-collapse-header {
    padding-top: 11px;
    padding-bottom: 11px;
    padding-left: 12px;
    font-weight: 500;
    color: #232426;

    .anticon {
      right: 12px;
    }
  }

  .ant-collapse-content-box {
    padding-left: 0;
    padding-right: 0;
  }
`;
